import { enhancedApi } from '@store/generated/jotcoach';

/**
 * Feature-specific tag types for jotcoach
 */
const JOTCOACH_TAG_TYPES = ['JotCoachSession', 'JotCoachSessionList'] as const;

/**
 * JotCoach API - extends the generated API with custom tagging
 */
export const jotcoachApi = enhancedApi.enhanceEndpoints({
  addTagTypes: JOTCOACH_TAG_TYPES,
  endpoints: {
    // Provider list endpoint
    getV2ProviderJotcoachSessions: {
      providesTags: result =>
        result
          ? [
              ...result.items.map(item => ({
                type: 'JotCoachSession' as const,
                id: item.jotcoach_session_id || 'unknown',
              })),
              { type: 'JotCoachSessionList' as const, id: 'LIST' },
            ]
          : [{ type: 'JotCoachSessionList', id: 'LIST' }],
    },
    // Admin list endpoint
    getV2AdminJotcoachSessions: {
      providesTags: result =>
        result
          ? [
              ...result.items.map(item => ({
                type: 'JotCoachSession' as const,
                id: item.jotcoach_session_id || 'unknown',
              })),
              { type: 'JotCoachSessionList' as const, id: 'LIST' },
            ]
          : [{ type: 'JotCoachSessionList', id: 'LIST' }],
    },
    // Individual session endpoints
    getV2ProviderJotcoachSessionsBySessionId: {
      providesTags: (_result, _error, arg) => [
        { type: 'JotCoachSession', id: arg.sessionId },
      ],
    },
    getV2AdminJotcoachSessionsBySessionId: {
      providesTags: (_result, _error, arg) => [
        { type: 'JotCoachSession', id: arg.sessionId },
      ],
    },
    getV2ProviderJotcoachSessionsByNoteByNoteId: {
      providesTags: result =>
        result ? [{ type: 'JotCoachSession', id: result.jotcoach_session_id }] : [],
    },
    // Encounter session endpoints
    getV2ProviderJotcoachEncounterSessions: {
      providesTags: result =>
        result
          ? [
              ...result.items.map(item => ({
                type: 'JotCoachSession' as const,
                id: `encounter-${item.encounter_id}`,
              })),
              { type: 'JotCoachSessionList' as const, id: 'ENCOUNTER_LIST' },
            ]
          : [{ type: 'JotCoachSessionList', id: 'ENCOUNTER_LIST' }],
    },
    getV2ProviderJotcoachEncounterSessionsByEncounterId: {
      providesTags: (_result, _error, arg) => [
        { type: 'JotCoachSession', id: `encounter-${arg.encounterId}` },
      ],
    },
    // Delete endpoint
    deleteV2AdminJotcoachSessionsBySessionId: {
      invalidatesTags: (_result, _error, arg) => [
        { type: 'JotCoachSession', id: arg.sessionId },
        { type: 'JotCoachSessionList', id: 'LIST' },
      ],
    },
    // Feedback endpoint
    postV2ProviderJotcoachSessionsBySessionIdFeedback: {
      invalidatesTags: (_result, _error, arg) => [
        { type: 'JotCoachSession', id: arg.sessionId },
      ],
    },
  },
});

// Export hooks with user-friendly names
export const {
  // Provider endpoints
  useGetV2ProviderJotcoachSessionsQuery: useGetJotCoachSessionsQuery,
  useGetV2ProviderJotcoachSessionsBySessionIdQuery: useGetJotCoachSessionQuery,
  useGetV2ProviderJotcoachSessionsByNoteByNoteIdQuery: useGetJotCoachSessionByNoteQuery,
  useLazyGetV2ProviderJotcoachSessionsByNoteByNoteIdQuery: useLazyGetJotCoachSessionByNoteQuery,
  useGetV2ProviderJotcoachEncounterSessionsQuery: useGetJotCoachEncounterSessionsQuery,
  useGetV2ProviderJotcoachEncounterSessionsByEncounterIdQuery: useGetJotCoachEncounterSessionQuery,
  useLazyGetV2ProviderJotcoachEncounterSessionsByEncounterIdQuery: useLazyGetJotCoachEncounterSessionQuery,
  useGetV2ProviderJotcoachRulesQuery: useGetJotCoachRulesQuery,
  usePostV2ProviderJotcoachSessionsBySessionIdFeedbackMutation: useSubmitJotCoachFeedbackMutation,
  // Admin endpoints
  useGetV2AdminJotcoachSessionsQuery: useGetAdminJotCoachSessionsQuery,
  useGetV2AdminJotcoachSessionsBySessionIdQuery: useGetAdminJotCoachSessionQuery,
  useDeleteV2AdminJotcoachSessionsBySessionIdMutation: useDeleteJotCoachSessionMutation,
} = jotcoachApi;
